import { Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CollaborateTaskData } from 'types/api/collaborate';
import { Page } from 'types/api/site';
import { BorderBeam, ShineBorder } from 'ui';
import Badge from 'ui/components/Badge';
import Button from 'ui/components/Button';
import { cn } from 'utils/cn';
import { useResponsive } from 'utils/useMediaQuery';
import { DesignReviewIndicator } from '../../tasks/design-review-indicator';
import ScrollingScreenshot from '../../tasks/scrolling-screenshot';
import { StickerTypingIndicator } from '../../tasks/sticker-typing-indicator';

interface Props {
  task?: Partial<CollaborateTaskData>;
  index?: string | number;
  pages?: Page[];
  isActiveSticker?: boolean;
  isGenerating?: boolean;
}

export const AIReviewTaskLoading = ({ isActiveSticker }: Props) => {
  const { t } = useTranslation();

  const { isDesktop } = useResponsive();

  return (
    <>
      <Button
        variant="ghost"
        tooltipSide={isDesktop ? 'right' : 'bottom'}
        className={cn(
          'flex w-full gap-2 rounded-lg p-2 bg-background relative border border-transparent flex-col h-auto',
        )}
      >
        <div className='w-full flex gap-2'>
          <div className='absolute inset-0 rounded-lg overflow-hidden'>
            <ShineBorder borderWidth={2} duration={12} shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />
            <BorderBeam
              duration={4}
              size={125}
              borderWidth={2}
              className="from-transparent via-tertiary to-transparent"
            />
            <BorderBeam
              duration={4}
              delay={2}
              size={125}
              borderWidth={2}
              className="from-transparent via-ata-orange to-transparent"
            />
          </div>
          <div className="flex h-full w-8 flex-col items-center justify-between">
            <div className='relative'>
              <div
                className="bg-tertiary relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-[15%_50%_50%_50%] p-1 outline outline-2 outline-white"
              >
                <span className='absolute bg-[#B07DF6] rounded-xl -bottom-2 h-6 w-8 z-1 blur' />
                <span className='absolute bg-[#D5BCF8] rounded-xl -bottom-2.5 h-4 w-6 z-1 blur-sm z-10' />
                <span className="relative z-10 text-xs font-normal text-white">
                  <Sparkles size={16} aria-label={t('task_sticker.aria.ai_generated', 'Ai Task Generated')} />
                </span>
              </div>
              {isActiveSticker && <StickerTypingIndicator />}
            </div>
          </div>
          <div className="flex h-full flex-1 flex-col gap-1 overflow-hidden text-left">
            <div className="leading-0 inline-flex items-center text-sm gap-2">
              <span className="truncate font-medium max-w-[130px]">InnerCircle</span>
              <Badge variant="generating" className="ml-auto">{t('task.ai.badge', 'Thinking')}</Badge>
            </div>
            <DesignReviewIndicator
              className="text-wrap text-sm font-normal max-w-[200px] truncate items-start mt-0 leading-0"
              indicatorClassName="hidden"
            />
          </div>
        </div>
        <ScrollingScreenshot />
      </Button>
    </>
  );
};